// src/views/AdminUsers.jsx
import React from "react";
import { listUsers, getUser } from "../api/admin";
import { RightSheet } from "./AdminUserDetail";

function RoleBadge({ r }) {
  const stop = (e) => e.stopPropagation(); // not clickable, avoid bubbling
  return (
    <span className="badge role-badge" onClick={stop} onMouseDown={stop} title={r}>
      {r}
    </span>
  );
}

export default function AdminUsers() {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [rows, setRows] = React.useState([]);

  // UI state
  const [q, setQ] = React.useState("");
  const [sortDir, setSortDir] = React.useState("asc");

  // Detail panel state
  const [selectedId, setSelectedId] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [detailError, setDetailError] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  // Load list
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await listUsers();
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) setErr(e.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load detail when selectedId changes
  React.useEffect(() => {
    let alive = true;
    if (!selectedId) return;
    (async () => {
      try {
        setDetailLoading(true);
        setDetailError(null);
        const u = await getUser(selectedId);
        if (alive) setDetail(u || null);
      } catch (e) {
        if (alive) setDetailError(e.message || String(e));
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  if (loading) return <p>Loading users…</p>;
  if (err)     return <p className="error">Error: {err}</p>;

  // filter + sort
  const filtered = rows.filter(u => {
    const hay = `${u.username} ${u.full_name || ""} ${u.email || ""} ${(u.roles||[]).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const sorted = [...filtered].sort((a,b) => {
    const cmp = (a.username || "").localeCompare(b.username || "", undefined, { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>User Admin</h2>
        <div className="tools">
          <input
            className="input"
            placeholder="Search username, email, role…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <button
            className="btn btn-ghost"
            onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
            aria-label={`Sort ${sortDir === "asc" ? "descending" : "ascending"}`}
          >
            Sort: {sortDir === "asc" ? "A→Z" : "Z→A"}
          </button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <p>No matching users.</p>
      ) : (
        <div className="table-wrap">
          <table className="table table-hover table-tight table-fixed admin-users">
            <colgroup>
              <col style={{ width: '18%' }} />  {/* User name */}
              <col style={{ width: '22%' }} />  {/* Full name */}
              <col style={{ width: '28%' }} />  {/* Email */}
              <col style={{ width: '10%' }} />  {/* Country */}
              <col />                           {/* Roles (flex) */}
              <col style={{ width: '10%' }} />  {/* Actions */}
            </colgroup>
            <thead>
              <tr>
                <th>User name</th>
                <th>Full name</th>
                <th>Email</th>
                <th>Country</th>
                <th>Roles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(u => (
                <tr key={u.user_id}>
                  <td className="truncate">{u.username}</td>
                  <td className="truncate">{u.full_name || '—'}</td>
                  <td className="truncate">{u.email || '—'}</td>
                  <td className="truncate">{u.country || '—'}</td>
                  <td>
                    <div className="roles-wrap">
                      {Array.isArray(u.roles) && u.roles.length
                        ? u.roles.map(r => <RoleBadge key={r} r={r} />)
                        : '—'}
                    </div>
                  </td>
                  <td className="center">
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => setSelectedId(u.user_id)}
                      aria-label={`View ${u.username}`}
                      title="View details"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Right-side detail sheet */}
      {selectedId && (
        <RightSheet
          title={detail?.username ? `User: ${detail.username}` : "User details"}
          onClose={() => { setSelectedId(null); setDetail(null); }}
        >
          {detailLoading && <p>Loading…</p>}
          {detailError && <p className="error">Error: {detailError}</p>}
          {!detailLoading && !detailError && detail && (
            <>
              <dl className="kv">
                <dt>User ID</dt><dd>{detail.user_id}</dd>
                <dt>Username</dt><dd>{detail.username}</dd>
                <dt>Full name</dt><dd>{detail.full_name || "—"}</dd>
                <dt>Email</dt><dd>{detail.email || "—"}</dd>
                <dt>Phone</dt><dd>{detail.phone_number || "—"}</dd>
                <dt>Address</dt><dd>{detail.address || "—"}</dd>
                <dt>Country</dt><dd>{detail.country || "—"}</dd>
                <dt>Roles</dt>
                <dd>
                  <div className="roles-wrap">
                    {Array.isArray(detail.roles) && detail.roles.length
                      ? detail.roles.map(r => <RoleBadge key={r} r={r} />)
                      : "—"}
                  </div>
                </dd>
              </dl>
            </>
          )}
        </RightSheet>
      )}
    </div>
  );
}